const PROTECTION_STYLE_ID = "branding-overlap-protection";

const HOST_STYLES: [string, string][] = [
  ["display", "block"],
  ["visibility", "visible"],
  ["opacity", "1"],
  ["position", "relative"],
  ["z-index", "2147483647"],
  ["width", "100%"],
  ["direction", "ltr"],
  ["isolation", "isolate"],
  ["clip-path", "none"],
  ["mask", "none"],
  ["filter", "none"],
  ["transform", "none"],
];

export function applyHostProtection(el: HTMLElement): void {
  const s = el.style;
  for (let i = 0; i < HOST_STYLES.length; i++) {
    s.setProperty(HOST_STYLES[i][0], HOST_STYLES[i][1], "important");
  }
}

export function startBrandingOverlapDetection(
  hostElement: HTMLElement,
): () => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let isBrandingVisible = false;

  const runDetection = () => {
    if (!hostElement.isConnected) return;
    detectAndProtect(hostElement);
  };

  const scheduleDetection = () => {
    if (debounceTimer) return;
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      runDetection();
    }, 150);
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        isBrandingVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      }
      if (isBrandingVisible) scheduleDetection();
    },
    { threshold: [0, 0.5, 1.0] },
  );
  io.observe(hostElement);

  const headObserver = new MutationObserver((mutations) => {
    if (!isBrandingVisible) return;
    for (const m of mutations) {
      for (const node of Array.from(m.addedNodes)) {
        if (
          node.nodeName === "STYLE" ||
          (node.nodeName === "LINK" &&
            (node as HTMLLinkElement).rel === "stylesheet")
        ) {
          scheduleDetection();
          return;
        }
      }
    }
  });

  if (document.head) {
    headObserver.observe(document.head, { childList: true });
  }

  const safetyInterval = setInterval(() => {
    if (!hostElement.isConnected) {
      clearInterval(safetyInterval);
      return;
    }
    if (isBrandingVisible) runDetection();
  }, 3000);

  setTimeout(runDetection, 500);

  return () => {
    io.disconnect();
    headObserver.disconnect();
    clearInterval(safetyInterval);
    if (debounceTimer) clearTimeout(debounceTimer);
    const style = document.getElementById(PROTECTION_STYLE_ID);
    if (style) style.remove();
  };
}

function detectAndProtect(host: HTMLElement): void {
  const rect = host.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  try {
    const topEl = document.elementFromPoint(cx, cy);
    if (!topEl) return;
    if (topEl === host || host.contains(topEl)) return;

    const htmlEl = topEl as HTMLElement;
    if (htmlEl.contains(host)) {
      fixAncestorPseudoOverlay(htmlEl);
    } else {
      fixElementOverlay(htmlEl);
    }
  } catch (e) {}

  const corners = [
    { x: rect.left + 10, y: rect.top + 3 },
    { x: rect.right - 10, y: rect.bottom - 3 },
  ];

  for (const { x, y } of corners) {
    try {
      const topEl = document.elementFromPoint(x, y);
      if (!topEl) continue;
      if (topEl === host || host.contains(topEl)) continue;

      const htmlEl = topEl as HTMLElement;
      if (htmlEl.contains(host)) {
        fixAncestorPseudoOverlay(htmlEl);
      } else {
        fixElementOverlay(htmlEl);
      }
    } catch (e) {}
  }
}

function fixElementOverlay(coveringEl: HTMLElement): void {
  if (coveringEl === document.body || coveringEl === document.documentElement)
    return;
  if (
    coveringEl.hasAttribute("data-branding-protected") ||
    coveringEl.closest("[data-branding-protected]")
  )
    return;

  try {
    const cs = getComputedStyle(coveringEl);
    if (
      (cs.position === "absolute" || cs.position === "fixed") &&
      parseInt(cs.zIndex, 10) > 100
    ) {
      coveringEl.style.setProperty("display", "none", "important");
    }
  } catch (e) {}
}

function fixAncestorPseudoOverlay(ancestor: HTMLElement): void {
  for (const pseudo of ["::before", "::after"] as const) {
    try {
      const cs = getComputedStyle(ancestor, pseudo);
      if (
        cs.content &&
        cs.content !== "none" &&
        cs.content !== "normal" &&
        (cs.position === "absolute" || cs.position === "fixed")
      ) {
        ancestor.setAttribute("data-branding-overlay-fix", "true");
        ensureProtectionStyle(pseudo);
      }
    } catch (e) {}
  }
}

let protectionStyleEl: HTMLStyleElement | null = null;

function ensureProtectionStyle(pseudo: string): void {
  if (!protectionStyleEl || !protectionStyleEl.isConnected) {
    protectionStyleEl = document.getElementById(
      PROTECTION_STYLE_ID,
    ) as HTMLStyleElement;
    if (!protectionStyleEl) {
      protectionStyleEl = document.createElement("style");
      protectionStyleEl.id = PROTECTION_STYLE_ID;
      document.head.appendChild(protectionStyleEl);
    }
  }

  const rule = `[data-branding-overlay-fix]${pseudo} { z-index: auto !important; }`;
  if (!protectionStyleEl.textContent?.includes(rule)) {
    protectionStyleEl.textContent =
      (protectionStyleEl.textContent || "") + "\n" + rule;
  }
}

export function sanitizeBrandingCss(_targetStyleId?: string): void {}
export function observeAndSanitizeBrandingCss(): () => void {
  return () => {};
}
export default sanitizeBrandingCss;
